import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import AuthShell from "./AuthShell";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inputCls, Field, GoldButton } from "@/components/app/widgets";

export function GoogleButton() {
  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      onClick={startGoogle}
      data-testid="google-login-button"
      className="flex w-full items-center justify-center gap-3 rounded-full border border-white/20 px-5 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
    >
      <svg className="h-4 w-4" viewBox="0 0 24 24">
        <path fill="#EA4335" d="M12 5.04c1.7 0 3.22.59 4.42 1.73l3.29-3.29C17.72 1.7 15.06.5 12 .5 7.37.5 3.34 3.16 1.42 7.04l3.83 2.97C6.18 7.1 8.86 5.04 12 5.04z"/>
        <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45c-.28 1.48-1.12 2.73-2.38 3.57l3.65 2.83c2.13-1.97 3.78-4.87 3.78-8.59z"/>
        <path fill="#FBBC05" d="M5.25 14.13a7.2 7.2 0 0 1 0-4.26L1.42 6.9a11.5 11.5 0 0 0 0 10.34l3.83-3.11z"/>
        <path fill="#34A853" d="M12 23.5c3.06 0 5.63-1.01 7.5-2.74l-3.65-2.83c-1.01.68-2.3 1.08-3.85 1.08-3.14 0-5.82-2.06-6.75-4.97l-3.83 3.1C3.34 20.98 7.37 23.5 12 23.5z"/>
      </svg>
      Continue with Google
    </button>
  );
}

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", form);
      setUser(res.data);
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      sub="Your AI business development department awaits."
      footer={
        <>
          New here?{" "}
          <Link to="/register" data-testid="goto-register-link" className="text-[#D4AF37] hover:brightness-110">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={submit} data-testid="login-form" className="space-y-5">
        <Field label="Email">
          <input
            type="email" required autoComplete="email" data-testid="login-email-input"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputCls} placeholder="you@company.com"
          />
        </Field>
        <Field label="Password">
          <input
            type="password" required autoComplete="current-password" data-testid="login-password-input"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={inputCls} placeholder="••••••••"
          />
        </Field>
        <GoldButton type="submit" disabled={loading} data-testid="login-submit-button" className="w-full py-3">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
        </GoldButton>
      </form>
      <div className="my-6 flex items-center gap-4">
        <span className="h-px flex-1 bg-white/10" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">or</span>
        <span className="h-px flex-1 bg-white/10" />
      </div>
      <GoogleButton />
      <p className="mt-5 text-center">
        <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-zinc-500 hover:text-[#D4AF37]">
          Forgot your password?
        </Link>
      </p>
    </AuthShell>
  );
}
